import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  Root as DialogRoot,
  Portal as DialogPortal,
  Overlay as DialogOverlay,
  Content as DialogContent,
  Title as DialogTitle,
  Description as DialogDescription,
} from "@radix-ui/react-dialog";
import { useCurrentUser, useAcceptTerms } from "../lib/auth/useAuth";
import { TERMS_SECTIONS, LEGAL_LAST_UPDATED } from "../content/legal";
import { Button } from "./ui/Button";

/** Sub-pixel scroll heights mean an exact equality check never fires. */
const SCROLL_EPSILON = 8;

/**
 * Blocks the authenticated app until the Terms of Use are accepted, and only
 * enables "I Agree" once they have actually been scrolled through.
 *
 * Deliberately not dismissible: Escape and outside clicks are swallowed, and
 * there is no close button. Signing out is the only way past it.
 */
export function TermsGate() {
  const { data: user } = useCurrentUser();
  const accept = useAcceptTerms();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasRead, setHasRead] = useState(false);

  const checkScrolledToEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // A tall viewport can fit the whole document with no scrollbar. Treat that
    // as read — otherwise the button could never be enabled.
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_EPSILON;
    if (atEnd) setHasRead(true);
  }, []);

  // Layout effect, not effect: measure before paint so a non-scrolling document
  // never flashes a disabled button.
  useLayoutEffect(() => {
    checkScrolledToEnd();
  }, [checkScrolledToEnd, user]);

  if (!user || user.termsAcceptedAt !== null) return null;

  return (
    <DialogRoot open>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-40 animate-fade-in bg-neutral-950/50 backdrop-blur-sm" />
        <DialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-4rem)] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col animate-fade-in rounded-3xl border border-neutral-100 bg-white shadow-glow focus:outline-none"
        >
          <div className="border-b border-neutral-100 p-6 pb-4">
            <DialogTitle className="font-display text-xl font-semibold text-neutral-900">
              Terms of Use
            </DialogTitle>
            <DialogDescription className="mt-1.5 text-sm text-neutral-600">
              Please read and accept these terms to continue. Last updated{" "}
              {LEGAL_LAST_UPDATED}.
            </DialogDescription>
          </div>

          <div
            ref={scrollRef}
            onScroll={checkScrolledToEnd}
            tabIndex={0}
            className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-neutral-700 focus:outline-none"
          >
            {TERMS_SECTIONS.map((section) => (
              <section key={section.heading}>
                <h3 className="font-semibold text-neutral-900">{section.heading}</h3>
                {section.body.map((para) => (
                  <p key={para} className="mt-2">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="border-t border-neutral-100 p-6 pt-4">
            {!hasRead && (
              <p className="mb-3 text-xs text-neutral-500">
                Scroll to the bottom to continue.
              </p>
            )}
            {accept.isError && (
              <p role="alert" className="mb-3 text-sm font-medium text-red-700">
                Couldn&apos;t record your acceptance. Please try again.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Link
                href="/terms"
                target="_blank"
                className="text-xs font-medium text-sky-700 hover:underline"
              >
                Open in a new tab
              </Link>
              <Button
                onClick={() => accept.mutate()}
                disabled={!hasRead}
                isLoading={accept.isPending}
              >
                I agree to the Terms of Use
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}
